import { useMemo } from 'react';
import { Box, Button, Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import Card from 'components/card/Card';
import LineChart from 'components/charts/LineChart';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { MdBarChart, MdOutlineCalendarToday } from 'react-icons/md';
import { RiArrowUpSFill, RiArrowDownSFill } from 'react-icons/ri';
import { useFinance } from 'contexts/FinanceContext';
import { formatAmount } from 'lib/finance';
import { lineChartOptionsTotalSpent } from 'variables/charts';

export default function FinanceTotalSpent(props: { [x: string]: any }) {
  const { ...rest } = props;
  const { totals, monthlyData, currencySymbol } = useFinance();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const iconColor = useColorModeValue('brand.500', 'white');
  const bgButton = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const bgHover = useColorModeValue({ bg: 'secondaryGray.400' }, { bg: 'whiteAlpha.50' });
  const bgFocus = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.100' });

  const isPositive = totals.balance >= 0;

  const chartData = useMemo(
    () => [
      { name: 'Income', data: monthlyData.map((m) => m.income) },
      { name: 'Expense', data: monthlyData.map((m) => m.expense) },
    ],
    [monthlyData]
  );

  const chartOptions = useMemo(
    () => ({
      ...lineChartOptionsTotalSpent,
      xaxis: {
        ...lineChartOptionsTotalSpent.xaxis,
        categories: monthlyData.map((m) => m.month),
      },
    }),
    [monthlyData]
  );

  return (
    <Card
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      w="100%"
      mb="0px"
      {...rest}
    >
      <Flex align="center" justify="space-between" w="100%" pe="20px" pt="5px">
        <Button
          bg={boxBg}
          fontSize="sm"
          fontWeight="500"
          color={textColorSecondary}
          borderRadius="7px"
        >
          <Icon as={MdOutlineCalendarToday} color={textColorSecondary} me="4px" />
          This month
        </Button>
        <Button
          ms="auto"
          alignItems="center"
          justifyContent="center"
          bg={bgButton}
          _hover={bgHover}
          _focus={bgFocus}
          _active={bgFocus}
          w="37px"
          h="37px"
          lineHeight="100%"
          borderRadius="10px"
        >
          <Icon as={MdBarChart} color={iconColor} w="24px" h="24px" />
        </Button>
      </Flex>
      <Flex w="100%" flexDirection={{ base: 'column', lg: 'row' }}>
        <Flex flexDirection="column" me="20px" mt="28px">
          <Text
            color={textColor}
            fontSize="34px"
            textAlign="start"
            fontWeight="700"
            lineHeight="100%"
          >
            {currencySymbol}
            {formatAmount(totals.totalExpense)}
          </Text>
          <Flex align="center" mb="20px">
            <Text
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
              mt="4px"
              me="12px"
            >
              Total Spent
            </Text>
            <Flex align="center">
              <Icon
                as={isPositive ? RiArrowUpSFill : RiArrowDownSFill}
                color={isPositive ? 'green.500' : 'red.500'}
                me="2px"
                mt="2px"
              />
              <Text
                color={isPositive ? 'green.500' : 'red.500'}
                fontSize="sm"
                fontWeight="700"
              >
                {currencySymbol}
                {formatAmount(Math.abs(totals.balance))}
              </Text>
            </Flex>
          </Flex>
          <Flex align="center">
            <Icon
              as={IoCheckmarkCircle}
              color={isPositive ? 'green.500' : 'red.500'}
              me="4px"
            />
            <Text
              color={isPositive ? 'green.500' : 'red.500'}
              fontSize="md"
              fontWeight="700"
            >
              {isPositive ? 'On track' : 'Over budget'}
            </Text>
          </Flex>
        </Flex>
        <Box minH="260px" minW="75%" mt="auto">
          {monthlyData.length > 0 ? (
            <LineChart chartData={chartData} chartOptions={chartOptions} />
          ) : (
            <Flex h="260px" align="center" justify="center" color="gray.400">
              No data yet
            </Flex>
          )}
        </Box>
      </Flex>
    </Card>
  );
}
